import React, { useEffect } from 'react';
import { createBrowserRouter, Navigate, useNavigate } from 'react-router-dom';
import { RouteConfig } from '../types/router';
import { ProtectedRoute } from './ProtectedRoute';
import { PublicRoute } from './PublicRoute';
import { useAuthStore } from '../stores/authStore';
import { authService } from '../services/authService';
import { clearSecureToken } from '../utils/secureStorage';

// Lazy load components for better performance
const LoginPage = React.lazy(() => import('../pages/LoginPage'));
const MainLayout = React.lazy(() => import('../layouts/MainLayout'));
const AppAccountManagement = React.lazy(() => import('../pages/AppAccountManagement'));
const Dashboard = React.lazy(() => import('../pages/Dashboard'));
const OrganizationManagement = React.lazy(() => import('../pages/OrganizationManagement'));
const OrganizationAdmins = React.lazy(() => import('../pages/OrganizationAdmins'));
const OrganizationMembers = React.lazy(() => import('../pages/OrganizationMembers'));
const MyOrganization = React.lazy(() => import('../pages/MyOrganization'));
const TeamManagement = React.lazy(() => import('../pages/TeamManagement'));
const OrderManagement = React.lazy(() => import('../pages/OrderManagement'));
const SubscriptionPlans = React.lazy(() => import('../pages/SubscriptionPlans'));
// 修正导入路径为 ContentSettings
const ContentSettings = React.lazy(() => import('../pages/ContentSettings.tsx'));
const FAQSettings = React.lazy(() => import('../pages/FAQSettings'));
const TestRecords = React.lazy(() => import('../pages/TestRecords'));
const MemberDashboard = React.lazy(() => import('../pages/member/MemberDashboard'));
const MemberTestRecords = React.lazy(() => import('../pages/member/MemberTestRecords'));
const MemberReport = React.lazy(() => import('../pages/member/MemberReport'));
const MyTeam = React.lazy(() => import('../pages/member/MyTeam'));
const SettingsPage = React.lazy(() => import('../pages/SettingsPage'));

const UnauthorizedPage = React.lazy(() => import('../pages/UnauthorizedPage'));
const NotFoundPage = React.lazy(() => import('../pages/NotFoundPage'));
const HtmlPolicyPage = React.lazy(() => import('../pages/HtmlPolicyPage'));

const XLogout: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const run = async () => {
      try {
        await authService.logout();
      } catch (e) { void 0; }
      try {
        useAuthStore.getState().logout();
      } catch (e) { void 0; }
      try {
        clearSecureToken();
      } catch (e) { void 0; }
      try {
        document.cookie.split(';').forEach(c => {
          const name = c.split('=')[0].trim();
          if (name) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
          }
        });
        localStorage.clear();
        sessionStorage.clear();
      } catch (e) { void 0; }
      navigate('/login', { replace: true });
    };
    run();
  }, [navigate]);
  return <div>Logging out...</div>;
};

// Route configuration based on design document
export const routeConfig: RouteConfig[] = [
  {
    path: '/',
    element: () => <Navigate to="/login" replace />,
    public: true,
    meta: {
      title: 'Home',
      hideInMenu: true,
      hideInBreadcrumb: true
    }
  },
  {
    path: '/html/:xtype',
    element: HtmlPolicyPage,
    public: true,
    meta: {
      title: 'Policy',
      hideInMenu: true,
      hideInBreadcrumb: true
    }
  },
  {
    path: '/xlogout',
    element: XLogout,
    public: true,
    meta: {
      title: 'Logout',
      hideInMenu: true,
      hideInBreadcrumb: true
    }
  },
  {
    path: '/login',
    element: LoginPage,
    public: true,
    meta: {
      title: 'Login',
      hideInMenu: true,
      hideInBreadcrumb: true
    }
  },
  {
    path: '/admin',
    element: MainLayout,
    roles: ['super_admin', 'admin'],
    children: [
      {
        path: '',
        element: () => <Navigate to="/admin/dashboard" replace />,
        roles: ['super_admin', 'admin']
      },
      {
        path: 'dashboard',
        element: Dashboard,
        roles: ['super_admin', 'admin'],
        meta: {
          title: 'Dashboard',
          icon: 'DashboardOutlined',
          order: 0
        }
      },
      {
        path: 'organizations',
        element: OrganizationManagement,
        roles: ['super_admin'],
        meta: {
          title: 'Organization Management',
          icon: 'BankOutlined',
          order: 1
        }
      },
      // 中文注释：恢复“机构管理员设定”页面路由（动态机构ID）
      // 路径示例：/organizations/123/admins
      // 仅超级管理员可访问；该页面不需要在侧边菜单显示
      {
        path: 'organizations/:organizationId/admins',
        element: OrganizationAdmins,
        roles: ['super_admin'],
        meta: {
          title: 'Organization Admins',
          hideInMenu: true
        }
      },
      // 中文注释：新增“机构账号列表”独立页面路由，与管理员设定保持一致的跳转行为
      {
        path: 'organizations/:organizationId/members',
        element: OrganizationMembers,
        roles: ['super_admin'],
        meta: {
          title: 'Organization Members',
          hideInMenu: true
        }
      },
      {
        path: 'my-organization',
        element: MyOrganization,
        roles: ['admin'],
        meta: {
          title: 'My Organization',
          icon: 'HomeOutlined',
          order: 1
        }
      },
      {
        path: 'teams',
        element: TeamManagement,
        roles: ['super_admin', 'admin'],
        meta: {
          title: 'Team Management',
          icon: 'TeamOutlined',
          order: 2
        }
      },
      {
        path: 'app-accounts',
        element: AppAccountManagement,
        roles: ['super_admin', 'admin'],
        meta: {
          title: 'App Account Management',
          icon: 'UserOutlined',
          order: 3
        }
      },
      {
        path: 'orders',
        element: OrderManagement,
        roles: ['super_admin'],
        meta: {
          title: 'Order Management',
          icon: 'ShoppingCartOutlined',
          order: 4
        }
      },
      {
        path: 'subscription-plans',
        element: SubscriptionPlans,
        roles: ['super_admin'],
        meta: {
          title: 'Subscription Plans',
          icon: 'SettingOutlined',
          order: 5
        }
      }
      ,
      {
        path: 'content-settings',
        element: ContentSettings,
        roles: ['super_admin'],
        meta: {
          title: 'Content Settings',
          icon: 'FileProtectOutlined',
          order: 6
        }
      }
      ,
      {
        path: 'faq-settings',
        element: FAQSettings,
        roles: ['super_admin'],
        meta: {
          title: 'FAQ Settings',
          icon: 'QuestionCircleOutlined',
          order: 7
        }
      }
      ,
      // 中文注释：新增“测试记录”页面路由，仅超级管理员可访问
      {
        path: 'test-records',
        element: TestRecords,
        roles: ['super_admin'],
        meta: {
          title: 'Test Records',
          icon: 'ExperimentOutlined',
          order: 8
        }
      },
      {
        path: 'settings',
        element: SettingsPage,
        roles: ['super_admin', 'admin'],
        meta: {
          title: 'Settings',
          icon: 'SettingOutlined',
          order: 9
        }
      }
    ]
  },
  {
    path: '/member',
    element: MainLayout,
    roles: ['member'],
    children: [
      {
        path: '',
        element: () => <Navigate to="/member/dashboard" replace />,
        roles: ['member']
      },
      {
        path: 'dashboard',
        element: MemberDashboard,
        roles: ['member'],
        meta: {
          title: 'Dashboard',
          icon: 'DashboardOutlined',
          order: 0
        }
      },
      {
        path: 'test-records',
        element: MemberTestRecords,
        roles: ['member'],
        meta: {
          title: 'Test Records',
          icon: 'ExperimentOutlined',
          order: 1
        }
      },
      {
        path: 'test-records/:detectionId/report',
        element: MemberReport,
        roles: ['member'],
        meta: {
          title: 'Report',
          hideInMenu: true
        }
      },
      {
        path: 'myteam',
        element: MyTeam,
        roles: ['member'],
        meta: {
          title: 'My Team',
          icon: 'TeamOutlined',
          order: 2
        }
      },
      {
        path: 'settings',
        element: SettingsPage,
        roles: ['member'],
        meta: {
          title: 'Settings',
          icon: 'SettingOutlined',
          order: 3
        }
      }
    ]
  },
  {
    path: '/unauthorized',
    element: UnauthorizedPage,
    public: true,
    meta: {
      title: 'Unauthorized',
      hideInMenu: true,
      hideInBreadcrumb: true
    }
  },
  {
    path: '*',
    element: NotFoundPage,
    public: true,
    meta: {
      title: 'Not Found',
      hideInMenu: true,
      hideInBreadcrumb: true
    }
  }
];

// Create route elements with proper guards
const createRouteElement = (config: RouteConfig): React.ReactElement => {
  const Component = config.element;
  
  if (config.public) {
    return (
      <PublicRoute>
        <React.Suspense fallback={<div>Loading...</div>}>
          <Component />
        </React.Suspense>
      </PublicRoute>
    );
  }

  return (
    <ProtectedRoute requiredRoles={config.roles} requiredOrgRoles={config.organizationRoles}>
      <React.Suspense fallback={<div>Loading...</div>}>
        <Component />
      </React.Suspense>
    </ProtectedRoute>
  );
};

// Convert route config to React Router format
const convertToRouterConfig = (configs: RouteConfig[]): any[] => {
  return configs.map(config => ({
    path: config.path,
    element: createRouteElement(config),
    children: config.children ? convertToRouterConfig(config.children) : undefined
  }));
};

// Create the router instance
export const router = createBrowserRouter(convertToRouterConfig(routeConfig));

// Export route utilities
export { routeConfig as routes };
export * from './ProtectedRoute';
export * from './PublicRoute';
